import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { 
  Wrench, 
  Hammer, 
  Paintbrush, 
  Frame, 
  Ruler, 
  Cog, 
  Cable, 
  CheckCircle,
  Star,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Calendar,
  Lock,
  Menu,
  X,
  ShoppingCart
} from 'lucide-react';
import { services, testimonials, stats } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';
import Shop from './Shop';
import CartSheet from './CartSheet';
import { useCart } from '../context/CartContext';

const iconMap = {
  Wrench, Hammer, Paintbrush, Frame, Ruler, Cog, Cable, CheckCircle
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveImg = (u) =>
  u && u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}/${u}`;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    carDetails: '',
    message: ''
  });

  const [gallery, setGallery] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { count } = useCart();

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((res) => setGallery(res.data))
      .catch((err) => console.error('Error loading gallery:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/quotes`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        car_details: formData.carDetails,
        message: formData.message
      });
      toast.success('Quote request submitted! We\'ll contact you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', carDetails: '', message: '' });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote. Please call us at 949 836 7727.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-gold/20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="h-16">
            <img 
              src="https://customer-assets.emergentagent.com/job_vintage-auto-1/artifacts/9lpdf0yd_IMG_8761.jpeg" 
              alt="OLD2NEW Logo" 
              className="h-full w-auto object-contain"
            />
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-white/80 hover:text-gold transition-colors">Services</a>
            <a href="#gallery" className="text-white/80 hover:text-gold transition-colors">Gallery</a>
            <a href="#shop" className="text-white/80 hover:text-gold transition-colors">Shop</a>
            <a href="#testimonials" className="text-white/80 hover:text-gold transition-colors">Reviews</a>
            <a href="#contact" className="text-white/80 hover:text-gold transition-colors">Contact</a>
            <a
              href="/admin"
              className="flex items-center gap-1.5 text-white/50 hover:text-gold transition-colors text-sm"
              data-testid="header-admin-login"
            >
              <Lock className="w-4 h-4" />
              Owner Login
            </a>
            <button
              onClick={() => setCartOpen(true)}
              className="relative text-white hover:text-gold transition-colors"
              aria-label="Open cart"
              data-testid="header-cart-button"
            >
              <ShoppingCart className="w-6 h-6" />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count-badge">
                  {count}
                </span>
              )}
            </button>
            <a href="tel:9498367727">
              <Button className="bg-gold text-black hover:bg-gold/90 font-semibold">
                <Phone className="w-4 h-4 mr-2" />
                949 836 7727
              </Button>
            </a>
          </nav>

          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-1">
            <button
              onClick={() => setCartOpen(true)}
              className="relative text-white p-2"
              aria-label="Open cart"
              data-testid="mobile-cart-button"
            >
              <ShoppingCart className="w-6 h-6" />
              {count > 0 && (
                <span className="absolute top-0 right-0 bg-gold text-black text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="text-white p-2"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {menuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {menuOpen && (
          <nav
            className="md:hidden bg-black/95 border-t border-gold/20 px-4 py-4 flex flex-col gap-1"
            data-testid="mobile-menu-panel"
          >
            <a onClick={() => setMenuOpen(false)} href="#services" className="text-white/80 hover:text-gold transition-colors py-3 border-b border-white/5">Services</a>
            <a onClick={() => setMenuOpen(false)} href="#gallery" className="text-white/80 hover:text-gold transition-colors py-3 border-b border-white/5">Gallery</a>
            <a onClick={() => setMenuOpen(false)} href="#shop" className="text-white/80 hover:text-gold transition-colors py-3 border-b border-white/5">Shop</a>
            <a onClick={() => setMenuOpen(false)} href="#testimonials" className="text-white/80 hover:text-gold transition-colors py-3 border-b border-white/5">Reviews</a>
            <a onClick={() => setMenuOpen(false)} href="#contact" className="text-white/80 hover:text-gold transition-colors py-3 border-b border-white/5">Contact</a>
            <a
              href="/admin"
              className="flex items-center gap-2 text-white/70 hover:text-gold transition-colors py-3 border-b border-white/5"
              data-testid="mobile-admin-login"
            >
              <Lock className="w-4 h-4" />
              Owner Login
            </a>
            <a href="tel:9498367727" className="mt-3">
              <Button className="w-full bg-gold text-black hover:bg-gold/90 font-semibold">
                <Phone className="w-4 h-4 mr-2" />
                949 836 7727
              </Button>
            </a>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gold/5 to-transparent" />
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-gold/10 text-gold border-gold/30 mb-6 text-sm px-4 py-2">
              Stanton, California • Since 2009
            </Badge>
            <h1 className="text-6xl md:text-7xl font-bold mb-6 tracking-tight">
              <span className="text-gold">OLD2NEW</span>
              <br />
              <span className="text-4xl md:text-5xl text-white/90">Classic Car Restoration</span>
            </h1>
            <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
              Bringing legendary classics back to life with expert craftsmanship, precision engineering, and passion for automotive excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-gold text-black hover:bg-gold/90 font-semibold text-lg px-8">
                Request Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-gold text-gold hover:bg-gold/10 font-semibold text-lg px-8">
                View Portfolio
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-6 bg-white/5 rounded-lg border border-white/10 hover:border-gold/50 transition-colors">
                <div className="text-4xl font-bold text-gold mb-2">{stat.value}</div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-white/5">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-gold">Services</span>
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Comprehensive restoration services for your classic automobile
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-black border-white/10 hover:border-gold/50 transition-all hover:transform hover:scale-105 cursor-pointer">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-gold" />
                    </div>
                    <CardTitle className="text-white text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-white/60">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <p className="text-white/70 text-lg mb-4">Need something else?</p>
            <Button variant="outline" size="lg" className="border-gold text-gold hover:bg-gold/10">
              Inquire About Custom Services
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-gold">Work</span>
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              See the transformations we've achieved
            </p>
          </div>
          
          <Tabs defaultValue="completed" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-white/5 mb-8">
              <TabsTrigger value="completed" className="data-[state=active]:bg-gold data-[state=active]:text-black">Completed</TabsTrigger>
              <TabsTrigger value="ongoing" className="data-[state=active]:bg-gold data-[state=active]:text-black">Ongoing Projects</TabsTrigger>
            </TabsList>
            
            <TabsContent value="completed" className="mt-8">
              <div className="grid md:grid-cols-2 gap-8">
                {gallery.filter(item => item.status === 'completed').map((item) => (
                  <Card key={item.id} className="bg-black border-white/10 overflow-hidden hover:border-gold/50 transition-colors">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative aspect-square">
                        <img src={resolveImg(item.before_image)} alt="Before" className="w-full h-full object-cover" />
                        <div className="absolute top-2 left-2 bg-black/80 text-gold px-3 py-1 rounded text-sm font-semibold">BEFORE</div>
                      </div>
                      <div className="relative aspect-square">
                        <img src={resolveImg(item.after_image)} alt="After" className="w-full h-full object-cover" />
                        <div className="absolute top-2 right-2 bg-gold text-black px-3 py-1 rounded text-sm font-semibold">AFTER</div>
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-white flex items-center justify-between">
                        {item.title}
                        <Badge className="bg-gold/10 text-gold border-gold/30">{item.year}</Badge>
                      </CardTitle>
                      <CardDescription className="text-white/60">{item.description}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="ongoing" className="mt-8">
              <div className="grid md:grid-cols-2 gap-8">
                {gallery.filter(item => item.status === 'ongoing').map((item) => (
                  <Card key={item.id} className="bg-black border-white/10 overflow-hidden hover:border-gold/50 transition-colors">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative aspect-square">
                        <img src={resolveImg(item.before_image)} alt="Before" className="w-full h-full object-cover" />
                        <div className="absolute top-2 left-2 bg-black/80 text-gold px-3 py-1 rounded text-sm font-semibold">BEFORE</div>
                      </div>
                      <div className="relative aspect-square">
                        <img src={resolveImg(item.after_image)} alt="Current Progress" className="w-full h-full object-cover" />
                        <div className="absolute top-2 right-2 bg-gold/20 text-gold px-3 py-1 rounded text-sm font-semibold">IN PROGRESS</div>
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-white flex items-center justify-between">
                        {item.title}
                        <Badge className="bg-gold/10 text-gold border-gold/30">Ongoing</Badge>
                      </CardTitle>
                      <CardDescription className="text-white/60">{item.description}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Shop Section */}
      <Shop />

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 bg-white/5">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              What Our <span className="text-gold">Clients Say</span>
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Real reviews from real customers
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-black border-white/10 hover:border-gold/50 transition-colors">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-white text-lg">{testimonial.name}</CardTitle>
                    <Badge variant="outline" className="border-gold/30 text-gold">{testimonial.source}</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-gold text-gold" />
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-white/70 italic mb-4">"{testimonial.review}"</p>
                  <p className="text-white/50 text-sm">{testimonial.date}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Request Form */}
      <section id="contact" className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Request a <span className="text-gold">Quote</span>
            </h2>
            <p className="text-white/70 text-lg">
              Tell us about your project and we'll get back to you within 24 hours
            </p>
          </div>
          <Card className="bg-black border-gold/30">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-white/80 text-sm mb-2 block">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="bg-white/5 border-white/10 text-white focus:border-gold"
                    />
                  </div>
                  <div>
                    <label className="text-white/80 text-sm mb-2 block">Email *</label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="bg-white/5 border-white/10 text-white focus:border-gold"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-white/80 text-sm mb-2 block">Phone *</label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(949) 836-7727"
                      className="bg-white/5 border-white/10 text-white focus:border-gold"
                    />
                  </div>
                  <div>
                    <label className="text-white/80 text-sm mb-2 block">Car Details *</label>
                    <Input
                      name="carDetails"
                      value={formData.carDetails}
                      onChange={handleChange}
                      required
                      placeholder="1967 Mustang Fastback"
                      className="bg-white/5 border-white/10 text-white focus:border-gold"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-white/80 text-sm mb-2 block">Project Details *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Tell us about your restoration project..."
                    className="bg-white/5 border-white/10 text-white focus:border-gold resize-none"
                  />
                </div>
                <Button type="submit" size="lg" className="w-full bg-gold text-black hover:bg-gold/90 font-semibold text-lg">
                  Submit Quote Request
                  <Calendar className="ml-2 h-5 w-5" />
                </Button>
                <p
                  data-testid="privacy-notice"
                  className="text-white/50 text-xs text-center leading-relaxed flex items-start justify-center gap-2"
                >
                  <Lock className="w-3.5 h-3.5 text-gold mt-0.5 shrink-0" />
                  <span>
                    Your privacy matters. The name, email, phone number, and project details you
                    provide are used solely to respond to your quote request. We never sell, rent,
                    or share your information with third parties, and you can ask us to delete your
                    details at any time by emailing{' '}
                    <a href="mailto:Old2newhb@gmail.com" className="text-gold hover:underline">
                      Old2newhb@gmail.com
                    </a>
                    .
                  </span>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white/5 border-t border-white/10 py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold text-gold mb-4">OLD2NEW</h3>
              <p className="text-white/60">Classic Car Restoration and Repair</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <div className="space-y-3 text-white/60">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gold" />
                  <a href="tel:9498367727" className="hover:text-gold transition-colors">949 836 7727</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gold" />
                  <a href="mailto:Old2newhb@gmail.com" className="hover:text-gold transition-colors">Old2newhb@gmail.com</a>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gold mt-1 shrink-0" />
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=8625+Central+Ave+Unit+J+Stanton+CA+90680"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gold transition-colors"
                    data-testid="footer-address-link"
                  >
                    8625 Central Ave Unit J, Stanton, CA 90680
                  </a>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Hours</h4>
              <div className="space-y-2 text-white/60">
                <p>Monday - Friday: 8AM - 6PM</p>
                <p>Saturday: 9AM - 4PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-white/50">
            <p>&copy; 2024 OLD2NEW Classic Car Restoration. All rights reserved.</p>
            <a
              href="/admin"
              className="text-white/30 hover:text-gold text-xs transition-colors mt-2 inline-block"
              data-testid="footer-admin-link"
            >
              Admin
            </a>
          </div>
        </div>
      </footer>

      <CartSheet open={cartOpen} onOpenChange={setCartOpen} />
    </div>
  );
};

export default Home;